import {
  faCheck,
  faDownload,
  faFileCode,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { message } from "antd";
import type React from "react";
import { useCallback, useId, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import uploadIcon from "../../../assets/upload-icon.png";
import { mergeMetadata } from "../../../utils/MergeMetadata";

const ACCEPT = ".json,.jsonld,application/json,application/ld+json";
const PREVIEW_CHAR_LIMIT = 12_000;

type SlotKind = "general" | "technical";

type FileMeta = {
  file: File;
  sizeLabel: string;
  detectedType: string | null;
  detectedKind: SlotKind | "unknown";
  softwareName: string | null;
  topKeys: string[];
  preview: string;
  error?: string;
};

type MergeResult = {
  document: Record<string, unknown>;
  filename: string;
  preview: string;
  summary: {
    generalType: string | null;
    technicalType: string | null;
    contextCount: number;
    topKeys: string[];
  };
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function truncatePreview(raw: string): string {
  if (raw.length <= PREVIEW_CHAR_LIMIT) return raw;
  return `${raw.slice(0, PREVIEW_CHAR_LIMIT)}\n\n… preview truncated (${formatBytes(raw.length)} total) …`;
}

function classifyKind(doc: Record<string, unknown>): SlotKind | "unknown" {
  const t = String(doc["@type"] ?? "");
  if (t === "API" || t === "TechnicalInterfaceInformation") return "technical";
  if (t === "EnergyResearchSoftware") return "general";
  if (
    "functions" in doc ||
    "apiFunctions" in doc ||
    "apiClasses" in doc ||
    "openapi" in doc
  ) {
    return "technical";
  }
  if ("softwareVersion" in doc || "identifier" in doc) return "general";
  const ctx = doc["@context"];
  const ctxStr = Array.isArray(ctx)
    ? ctx.map(String).join(" ")
    : typeof ctx === "string"
      ? ctx
      : "";
  if (/ersmeta/i.test(ctxStr)) return "general";
  if (/datadesc\.org/i.test(ctxStr)) return "technical";
  return "unknown";
}

async function inspectFile(file: File): Promise<FileMeta> {
  const text = await file.text();
  try {
    const doc = JSON.parse(text) as Record<string, unknown>;
    if (!doc || typeof doc !== "object" || Array.isArray(doc)) {
      throw new Error("Root value must be a JSON object.");
    }
    const pretty = JSON.stringify(doc, null, 2);
    const detectedKind = classifyKind(doc);
    return {
      file,
      sizeLabel: formatBytes(file.size),
      detectedType: doc["@type"] != null ? String(doc["@type"]) : null,
      detectedKind,
      softwareName:
        typeof doc.name === "string"
          ? doc.name
          : typeof (doc.info as { title?: string } | undefined)?.title ===
              "string"
            ? (doc.info as { title: string }).title
            : null,
      topKeys: Object.keys(doc).slice(0, 8),
      preview: truncatePreview(pretty),
    };
  } catch (err) {
    return {
      file,
      sizeLabel: formatBytes(file.size),
      detectedType: null,
      detectedKind: "unknown",
      softwareName: null,
      topKeys: [],
      preview: "",
      error: err instanceof Error ? err.message : "Invalid JSON / JSON-LD file.",
    };
  }
}

function buildResultSummary(document: Record<string, unknown>): MergeResult["summary"] {
  const general = document.generalSoftwareInformation as
    | Record<string, unknown>
    | undefined;
  const technical = document.technicalInterfaceInformation as
    | Record<string, unknown>
    | undefined;
  const ctx = document["@context"];
  return {
    generalType: general?.["@type"] != null ? String(general["@type"]) : null,
    technicalType:
      technical?.["@type"] != null ? String(technical["@type"]) : null,
    contextCount: Array.isArray(ctx) ? ctx.length : ctx ? 1 : 0,
    topKeys: Object.keys(document),
  };
}

const MergeMetadata: React.FC = () => {
  const [general, setGeneral] = useState<FileMeta | null>(null);
  const [technical, setTechnical] = useState<FileMeta | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<MergeResult | null>(null);
  const [showInputPreview, setShowInputPreview] = useState<{
    general: boolean;
    technical: boolean;
  }>({ general: false, technical: false });
  const [showResultPreview, setShowResultPreview] = useState(false);
  const [dragOver, setDragOver] = useState<SlotKind | null>(null);

  const generalInputId = useId();
  const technicalInputId = useId();
  const generalInputRef = useRef<HTMLInputElement>(null);
  const technicalInputRef = useRef<HTMLInputElement>(null);

  const canMerge = Boolean(
    general &&
      !general.error &&
      technical &&
      !technical.error &&
      !busy,
  );

  const mismatchHint = useMemo(() => {
    if (!general || !technical || general.error || technical.error) return null;
    if (
      general.detectedKind === "technical" &&
      technical.detectedKind === "general"
    ) {
      return "Looks like the files are swapped — that’s fine, merge will auto-detect.";
    }
    if (
      general.detectedKind === "general" &&
      technical.detectedKind === "technical"
    ) {
      return null;
    }
    if (general.detectedKind === "unknown" || technical.detectedKind === "unknown") {
      return "Couldn’t confidently detect types from @type/@context — merge will still try.";
    }
    return null;
  }, [general, technical]);

  const assignSlot = useCallback(
    async (kind: SlotKind, file: File | undefined) => {
      if (!file) return;
      const meta = await inspectFile(file);
      if (meta.error) {
        message.error(meta.error);
      }
      if (kind === "general") setGeneral(meta);
      else setTechnical(meta);
      setResult(null);
      setShowResultPreview(false);
    },
    [],
  );

  const onDrop = useCallback(
    (kind: SlotKind) => (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(null);
      const file = e.dataTransfer.files?.[0];
      void assignSlot(kind, file);
    },
    [assignSlot],
  );

  const clearSlot = (kind: SlotKind) => {
    if (kind === "general") {
      setGeneral(null);
      if (generalInputRef.current) generalInputRef.current.value = "";
    } else {
      setTechnical(null);
      if (technicalInputRef.current) technicalInputRef.current.value = "";
    }
    setResult(null);
  };

  const handleMerge = async () => {
    if (!general || !technical || general.error || technical.error) return;
    setBusy(true);
    setResult(null);
    setShowResultPreview(false);
    try {
      const res = await mergeMetadata(general.file, technical.file);
      const pretty = JSON.stringify(res.document, null, 2);
      setResult({
        document: res.document,
        filename: res.filename || "software_datadesc.jsonld",
        preview: truncatePreview(pretty),
        summary: buildResultSummary(res.document),
      });
      message.success("Merged successfully");
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : `Merge failed: ${error}`,
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result.document, null, 2)], {
      type: "application/ld+json",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = result.filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const renderSlot = (
    kind: SlotKind,
    meta: FileMeta | null,
    inputId: string,
    inputRef: React.RefObject<HTMLInputElement>,
  ) => {
    const label =
      kind === "general"
        ? "General metadata (ERSmeta)"
        : "Technical metadata (DataDesc)";
    const hint =
      kind === "general"
        ? "EnergyResearchSoftware JSON-LD"
        : "API JSON-LD";
    const isDragging = dragOver === kind;
    const previewOpen = showInputPreview[kind];

    return (
      <DropZone
        $active={isDragging}
        $filled={Boolean(meta && !meta.error)}
        $error={Boolean(meta?.error)}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(kind);
        }}
        onDragLeave={() => setDragOver(null)}
        onDrop={onDrop(kind)}
      >
        <DropHeader>
          <DropTitle>{label}</DropTitle>
          {meta && (
            <IconButton
              type="button"
              aria-label={`Remove ${label}`}
              onClick={() => clearSlot(kind)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          )}
        </DropHeader>

        {!meta ? (
          <EmptyState>
            <UploadButton htmlFor={inputId} aria-label={`Upload ${label}`}>
              <UploadIconImg src={uploadIcon} alt="" />
            </UploadButton>
            <p>Click the icon or drop here</p>
            <Hint>
              {hint} · <code>.json</code> / <code>.jsonld</code>
            </Hint>
          </EmptyState>
        ) : (
          <FileCard>
            <FileIconWrap $ok={!meta.error}>
              <FontAwesomeIcon icon={meta.error ? faTimes : faFileCode} />
            </FileIconWrap>
            <FileName title={meta.file.name}>{meta.file.name}</FileName>
            <FileMetaRow>
              <span>{meta.sizeLabel}</span>
              {meta.detectedType && (
                <TypePill $kind={meta.detectedKind}>
                  {meta.detectedType}
                </TypePill>
              )}
              {meta.softwareName && <span>{meta.softwareName}</span>}
            </FileMetaRow>
            {meta.error ? (
              <ErrorText>{meta.error}</ErrorText>
            ) : (
              <KeyRow>
                {meta.topKeys.map((k) => (
                  <KeyChip key={k}>{k}</KeyChip>
                ))}
              </KeyRow>
            )}
            {!meta.error && (
              <PreviewToggle
                type="button"
                onClick={() =>
                  setShowInputPreview((s) => ({ ...s, [kind]: !s[kind] }))
                }
              >
                {previewOpen ? "Hide preview" : "Show truncated preview"}
              </PreviewToggle>
            )}
          </FileCard>
        )}

        {meta && !meta.error && previewOpen && (
          <PreviewBlock>
            <pre>{meta.preview}</pre>
          </PreviewBlock>
        )}

        <HiddenInput
          ref={inputRef as React.RefObject<HTMLInputElement>}
          id={inputId}
          type="file"
          accept={ACCEPT}
          onChange={(e) => {
            void assignSlot(kind, e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </DropZone>
    );
  };

  return (
    <Page>
      <HeaderCard>
        <PageTitle>Merge Metadata</PageTitle>
        <PageSubtitle>
          Combine your general (ERSmeta) and technical (DataDesc) JSON-LD
          exports into one unified DataDesc document with both contexts.
        </PageSubtitle>
      </HeaderCard>

      <Slots>
        {renderSlot("general", general, generalInputId, generalInputRef)}
        {renderSlot("technical", technical, technicalInputId, technicalInputRef)}
      </Slots>

      {mismatchHint && <SoftHint>{mismatchHint}</SoftHint>}

      <Actions>
        <PrimaryButton
          type="button"
          disabled={!canMerge}
          onClick={() => void handleMerge()}
        >
            {busy ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Merging…
              </>
            ) : (
              "Merge to DataDesc Object"
            )}
        </PrimaryButton>
      </Actions>

      {result && (
        <ResultCard>
          <ResultTitle>
            <FontAwesomeIcon icon={faCheck} /> Merged document ready
          </ResultTitle>

          <PrimaryButton type="button" onClick={handleDownload}>
            <FontAwesomeIcon icon={faDownload} /> Download {result.filename}
          </PrimaryButton>

          <ResultMeta>
            <MetaItem>
              <MetaLabel>Filename</MetaLabel>
              <MetaValue>{result.filename}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Contexts</MetaLabel>
              <MetaValue>{result.summary.contextCount} linked</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>General</MetaLabel>
              <MetaValue>{result.summary.generalType ?? "—"}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Technical</MetaLabel>
              <MetaValue>{result.summary.technicalType ?? "—"}</MetaValue>
            </MetaItem>
          </ResultMeta>

          <KeyRow>
            {result.summary.topKeys.map((k) => (
              <KeyChip key={k}>{k}</KeyChip>
            ))}
          </KeyRow>

          <PreviewToggle
            type="button"
            onClick={() => setShowResultPreview((v) => !v)}
          >
            {showResultPreview
              ? "Hide preview"
              : "Show truncated preview (large files stay truncated)"}
          </PreviewToggle>
          {showResultPreview && (
            <PreviewBlock>
              <pre>{result.preview}</pre>
            </PreviewBlock>
          )}
        </ResultCard>
      )}
    </Page>
  );
};

const Page = styled.div`
  max-width: 960px;
`;

const HeaderCard = styled.div`
  background: var(--color-dashboard-box);
  border: 1px solid var(--color-stroke);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  letter-spacing: -0.02em;
`;

const PageSubtitle = styled.p`
  margin: 8px 0 0;
  font-size: 0.95rem;
  color: #666;
  line-height: 1.5;
  max-width: 52rem;
`;

const Slots = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const DropZone = styled.div<{
  $active: boolean;
  $filled: boolean;
  $error: boolean;
}>`
  position: relative;
  border: 2px dashed
    ${({ $active, $error, $filled }) =>
      $error
        ? "#c45c5c"
        : $active
          ? "var(--color-primary)"
          : $filled
            ? "var(--color-primary-desat)"
            : "var(--color-stroke)"};
  background: ${({ $active, $filled }) =>
    $active
      ? "rgba(var(--color-primary-rgb), 0.08)"
      : $filled
        ? "#fff"
        : "var(--color-dashboard-box)"};
  border-radius: 12px;
  padding: 16px;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  transition: border-color 0.15s, background 0.15s;
`;

const DropHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
`;

const DropTitle = styled.h2`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
`;

const IconButton = styled.button`
  && {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    min-width: 28px;
    margin: 0;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #888;
    font-size: 0.85rem;
    line-height: 1;
    cursor: pointer;

    &:hover,
    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.06);
      color: #333;
      border-color: transparent;
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 8px;
  padding: 12px 8px 8px;
  color: #555;
  font-size: 0.9rem;

  p {
    margin: 0;
  }

  code {
    font-size: 0.85em;
    background: rgba(0, 0, 0, 0.05);
    padding: 1px 5px;
    border-radius: 4px;
  }
`;

const UploadButton = styled.label`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    margin: 0;
    padding: 0;
    border: none;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    font-weight: inherit;
    font-size: inherit;
    line-height: 0;
    cursor: pointer;
    transition: transform 0.15s ease;

    &:hover,
    &:focus,
    &:focus-visible,
    &:active {
      background: transparent;
      box-shadow: none;
      outline: none;
      border: none;
      filter: none;
    }

    &:hover {
      transform: scale(1.06);
    }
  }
`;

const UploadIconImg = styled.img`
  width: 56px;
  height: auto;
  aspect-ratio: 79 / 65;
  display: block;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
`;

const Hint = styled.span`
  font-size: 0.8rem;
  color: #888;

  code {
    font-size: 0.85em;
    background: rgba(0, 0, 0, 0.05);
    padding: 1px 5px;
    border-radius: 4px;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const FileCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
`;

const FileIconWrap = styled.div<{ $ok: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  background: ${({ $ok }) =>
    $ok ? "rgba(var(--color-primary-rgb), 0.12)" : "rgba(196, 92, 92, 0.12)"};
  color: ${({ $ok }) => ($ok ? "var(--color-primary)" : "#c45c5c")};
`;

const FileName = styled.div`
  font-weight: 600;
  color: #1a1a1a;
  font-size: 0.95rem;
  line-height: 1.35;
  word-break: break-word;
  max-width: 100%;
`;

const FileMetaRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.8rem;
  color: #666;
  width: 100%;
`;

const TypePill = styled.span<{ $kind: SlotKind | "unknown" }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${({ $kind }) =>
    $kind === "general"
      ? "rgba(38, 170, 99, 0.15)"
      : $kind === "technical"
        ? "rgba(54, 129, 106, 0.18)"
        : "rgba(0, 0, 0, 0.08)"};
  color: ${({ $kind }) =>
    $kind === "unknown" ? "#666" : "var(--color-dark-green)"};
`;

const KeyRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
`;

const KeyChip = styled.span`
  font-size: 0.72rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  padding: 4px 10px;
  border-radius: 4px;
  background: #f0f4f1;
  color: #445;
  border: 1px solid var(--color-stroke);
`;

const ErrorText = styled.p`
  margin: 0;
  color: #c45c5c;
  font-size: 0.85rem;
`;

const PreviewToggle = styled.button`
  && {
    flex: 0 0 auto;
    width: fit-content;
    max-width: 100%;
    margin: 0;
    border: none;
    background: transparent;
    padding: 8px 12px;
    color: var(--color-primary);
    font-size: 0.85rem;
    font-weight: 600;
    line-height: 1.3;
    cursor: pointer;
    text-align: left;
    border-radius: 6px;

    &:hover,
    &:hover:not(:disabled) {
      text-decoration: underline;
      background: rgba(var(--color-primary-rgb), 0.06);
      color: var(--color-primary);
      border-color: transparent;
    }
  }
`;

const PreviewBlock = styled.div`
  max-height: 220px;
  overflow: auto;
  border-radius: 8px;
  border: 1px solid var(--color-stroke);
  background: #fafcfb;

  pre {
    margin: 0;
    padding: 12px;
    font-size: 0.75rem;
    line-height: 1.45;
    white-space: pre-wrap;
    word-break: break-word;
    color: #2a332e;
  }
`;

const SoftHint = styled.p`
  margin: 0 0 12px;
  font-size: 0.85rem;
  color: #666;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const PrimaryButton = styled.button`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex: 0 0 auto;
    width: auto;
    max-width: 100%;
    padding: 12px 22px;
    border: none;
    border-radius: 8px;
    background: var(--color-primary);
    color: #fff;
    font-weight: 600;
    font-size: 0.95rem;
    line-height: 1.3;
    cursor: pointer;
    transition: opacity 0.15s, background-color 0.15s;
    white-space: nowrap;

    &:hover:not(:disabled) {
      opacity: 0.92;
      background-color: var(--color-primary);
      color: #fff;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }
`;

const ResultCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  background: #fff;
  border: 1px solid var(--color-stroke);
  border-radius: 12px;
  padding: 28px;
`;

const ResultTitle = styled.h3`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--color-dark-green);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ResultMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--color-stroke);

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const MetaLabel = styled.div`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #888;
  font-weight: 600;
`;

const MetaValue = styled.div`
  font-size: 0.95rem;
  color: #1a1a1a;
  word-break: break-word;
  line-height: 1.4;
`;

export default MergeMetadata;
